import { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { emailExport } from '@/data/supabase';
import {
  customersToCsv, invoicesToCsv, stopsToCsv,
  customersToPdf, invoicesToPdf, stopsToPdf,
  downloadCustomersCsv, downloadInvoicesCsv, downloadStopsCsv,
  downloadCustomersPdf, downloadInvoicesPdf, downloadStopsPdf,
  pdfToBase64,
} from '@/lib/export';

const ENTITY_LABEL = {
  customers: 'Customers',
  invoices:  'Invoices',
  stops:     'Routes / Stops',
};

export default function ExportModal({ data }) {
  const { customers, invoices, stops, closeModal, userEmail, showToast } = useApp();
  const entity = data?.entity || 'customers';
  const [format,  setFormat]  = useState('csv');
  const [emailTo, setEmailTo] = useState(userEmail || '');
  const [busy,    setBusy]    = useState(false);

  const items = entity === 'customers' ? customers : entity === 'invoices' ? invoices : stops;
  const count = items.length;

  const handleDownload = async () => {
    if (format === 'csv') {
      if (entity === 'customers') return downloadCustomersCsv(customers);
      if (entity === 'invoices')  return downloadInvoicesCsv(invoices, customers);
      if (entity === 'stops')     return downloadStopsCsv(stops, customers);
    }
    if (format === 'pdf') {
      if (entity === 'customers') return downloadCustomersPdf(customers);
      if (entity === 'invoices')  return downloadInvoicesPdf(invoices, customers);
      if (entity === 'stops')     return downloadStopsPdf(stops, customers);
    }
  };

  const handleEmail = async () => {
    if (!emailTo) return;
    setBusy(true);

    const today = new Date().toISOString().slice(0, 10);
    const attachments = [];

    // Build attachments based on format
    if (format === 'csv' || format === 'both') {
      let csv = '';
      let filename = '';
      if (entity === 'customers') { csv = customersToCsv(customers); filename = `customers-${today}.csv`; }
      if (entity === 'invoices')  { csv = invoicesToCsv(invoices, customers); filename = `invoices-${today}.csv`; }
      if (entity === 'stops')     { csv = stopsToCsv(stops, customers); filename = `stops-${today}.csv`; }
      attachments.push({
        filename,
        content_base64: btoa(unescape(encodeURIComponent(csv))),
        content_type: 'text/csv',
      });
    }
    if (format === 'pdf' || format === 'both') {
      let doc;
      let filename = '';
      if (entity === 'customers') { doc = await customersToPdf(customers); filename = `customers-${today}.pdf`; }
      if (entity === 'invoices')  { doc = await invoicesToPdf(invoices, customers); filename = `invoices-${today}.pdf`; }
      if (entity === 'stops')     { doc = await stopsToPdf(stops, customers); filename = `stops-${today}.pdf`; }
      attachments.push({
        filename,
        content_base64: pdfToBase64(doc),
        content_type: 'application/pdf',
      });
    }

    const res = await emailExport({
      to: emailTo,
      subject: `SteelOps ${ENTITY_LABEL[entity]} export — ${today}`,
      body: `Your ${ENTITY_LABEL[entity]} export is attached (${count} record${count === 1 ? '' : 's'}).\n\nGenerated ${new Date().toLocaleString()}.`,
      attachments,
    });
    setBusy(false);
    if (res.ok) {
      showToast(`Export emailed to ${emailTo} ✓`);
      closeModal();
    } else {
      showToast(res.error || 'Email failed', false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (delivery === 'download') {
      await handleDownload();
      closeModal();
    } else {
      await handleEmail();
    }
  };

  const [delivery, setDelivery] = useState('download');

  return (
    <div className="modal" style={{ width: 'min(480px, 95vw)' }}>
      <div className="modal-head">
        <span className="modal-title">Export {ENTITY_LABEL[entity]}</span>
        <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
      </div>
      <div className="modal-body">
        <div style={{ fontSize: 12.5, color: 'var(--t2)', marginBottom: 14 }}>
          Exporting <strong style={{ color: 'var(--t1)' }}>{count}</strong> {ENTITY_LABEL[entity].toLowerCase()} record{count === 1 ? '' : 's'}.
        </div>

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>Format</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { v: 'csv', l: 'CSV' },
                { v: 'pdf', l: 'PDF' },
                { v: 'both', l: 'Both' },
              ].map(o => (
                <button
                  key={o.v}
                  type="button"
                  className={`btn btn-sm ${format === o.v ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setFormat(o.v)}
                  disabled={o.v === 'both' && delivery === 'download'}
                  title={o.v === 'both' && delivery === 'download' ? 'Both formats only available via email' : ''}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label>Delivery</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className={`btn btn-sm ${delivery === 'download' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => { setDelivery('download'); if (format === 'both') setFormat('csv'); }}
              >
                ⬇️ Download
              </button>
              <button
                type="button"
                className={`btn btn-sm ${delivery === 'email' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setDelivery('email')}
              >
                ✉️ Email
              </button>
            </div>
          </div>

          {delivery === 'email' && (
            <div className="field">
              <label>Send to</label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="name@email.com"
                required
              />
            </div>
          )}

          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Sending…' : delivery === 'download' ? 'Download' : 'Send email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
